/**
 * LiveStatsStrip — three micro-метрики (TPS / Latency / Active)
 * под orbit, числа обновляются каждые ~2с.
 *
 * Значения приходят с бэка через GET /api/luna/dashboard/stats.
 * Сервер держит единый state в памяти и тикает его на 2 с, поэтому
 * ВСЕ подключённые юзеры видят одну и ту же картину — никаких
 * клиентских Math.random() расхождений.
 *
 * Подаёт сигнал "система живая" минимальным шумом, профессиональный
 * биржевой/мониторинговый вайб.
 */
import { memo } from 'react';
import {
  useConnectionStatus,
  type ConnectionQuality,
} from '@/contexts/ConnectionStatusContext';
import { useDashboardStats } from '@/hooks/useDashboardStats';

// Map a metric value to a quality level. Each metric has its own
// thresholds since "good TPS" and "good latency" are scaled differently.
function tpsQuality(v: number): ConnectionQuality {
  if (v >= 780) return 'good';
  if (v >= 580) return 'warn';
  return 'bad';
}

function latQuality(v: number): ConnectionQuality {
  if (v <= 55) return 'good';
  if (v <= 110) return 'warn';
  return 'bad';
}

function upQuality(v: number): ConnectionQuality {
  if (v === 7) return 'good';
  if (v >= 5) return 'warn';
  return 'bad';
}

// Visual style per connection quality.
const STATUS_STYLE: Record<ConnectionQuality, {
  color: string;
  pulse: string;
  glow: string;
}> = {
  good: {
    color: 'bg-[var(--status-positive)]',
    pulse: 'animate-status-pulse-slow',
    glow: '0 0 6px color-mix(in oklab, var(--status-positive) 75%, transparent)',
  },
  warn: {
    color: 'bg-[var(--status-warning)]',
    pulse: 'animate-status-pulse-fast',
    glow: '0 0 8px color-mix(in oklab, var(--status-warning) 80%, transparent)',
  },
  bad: {
    color: 'bg-[var(--status-negative)]',
    pulse: 'animate-status-pulse-urgent',
    glow: '0 0 10px color-mix(in oklab, var(--status-negative) 85%, transparent)',
  },
};

const LINK_LABEL: Record<ConnectionQuality, string> = {
  good: 'LINK',
  warn: 'LAG',
  bad: 'DROP',
};

function StatusDot({ quality }: { quality: ConnectionQuality }) {
  const style = STATUS_STYLE[quality];
  return (
    <span
      className={`inline-flex h-1.5 w-1.5 rounded-full flex-shrink-0 ${style.color} ${style.pulse}`}
      style={{ boxShadow: style.glow }}
    />
  );
}

function LiveStatsStripImpl() {
  const status = useConnectionStatus();
  const stats = useDashboardStats();

  // Loading / fallback values — keep the bar looking alive before the
  // first server response lands. Once stats arrive every client sees
  // the same numbers because they come from a single server process.
  const tps = stats?.tps ?? 847;
  const latency = stats?.latency ?? 42;
  const active = stats?.active ?? 7;

  // Each metric carries its OWN dot — quality is computed from the
  // current value, so as TPS / LAT drift the dots change colour
  // independently. LINK uses the global ConnectionStatus directly.
  const tpsQ = tpsQuality(tps);
  const latQ = latQuality(latency);
  const upQ = upQuality(active);

  return (
    <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--text-disabled)] px-3 py-1">
      <span className="flex items-center gap-1">
        <StatusDot quality={status} />
        <span className="text-[var(--text-disabled)]">{LINK_LABEL[status]}</span>
      </span>
      <span className="text-[var(--text-disabled)]">·</span>
      <span className="flex items-center gap-1">
        <StatusDot quality={tpsQ} />
        <span className="text-[var(--text-disabled)]">TPS</span>
        <span className="text-[var(--text-secondary)] tabular-nums">
          {tps}
        </span>
      </span>
      <span className="text-[var(--text-disabled)]">·</span>
      <span className="flex items-center gap-1">
        <StatusDot quality={latQ} />
        <span className="text-[var(--text-disabled)]">LAT</span>
        <span className="text-[var(--text-secondary)] tabular-nums">
          {latency}ms
        </span>
      </span>
      <span className="text-[var(--text-disabled)]">·</span>
      <span className="flex items-center gap-1">
        <StatusDot quality={upQ} />
        <span className="text-[var(--text-disabled)]">UP</span>
        <span className="text-[var(--status-positive)] tabular-nums">
          {active}/7
        </span>
      </span>
    </div>
  );
}

export const LiveStatsStrip = memo(LiveStatsStripImpl);
